import { createSlice } from "@reduxjs/toolkit";

const initialState = {
    // ── Triggers ──────────────────────────────────────────────────────────────
    // [{ id, datasetId, name, frequency, runTime, timezone, runOn, status, nextRunAt, recipients, createdAt, updatedAt }]
    triggers: [],
    triggersLoading: false,
    triggerSaving: false,
    manualExportLoading: false,

    // ── Run History ───────────────────────────────────────────────────────────
    // [{ id, datasetId, triggerId, triggerName, runMode, runStatus,
    //    initiatedAt, completedAt, s3Key, urlExpiresAt, errorMessage }]
    runs: [],
    runsLoading: false,
    runsPagination: {
        page: 1,        // 0-based, matches API
        limit: 10,
        total: 0,
        totalPages: 0
    },

    // Filters survive tab switches
    runsFilters: {
        runMode: "",    // "" | "MANUAL" | "SCHEDULED"
        runStatus: "",  // "" | "QUEUED" | "PROCESSING" | "SUCCESS" | "FAILED" | "EXPIRED"
        triggerId: "",  // "" | number string
    },

    // Single run for detail dialog
    detailRun: null,
    detailLoading: false,

    // Per-run download loading: { [runId]: boolean }
    downloadLoadingByRun: {},
};

const exportTriggerSlice = createSlice({
    name: "exportTrigger",
    initialState,
    reducers: {

        // ── Triggers ──────────────────────────────────────────────────────────

        setTriggersLoading(state, action) {
            state.triggersLoading = action.payload;
        },
        setTriggerSaving(state, action) {
            state.triggerSaving = action.payload;
        },
        setManualExportLoading(state, action) {
            state.manualExportLoading = action.payload;
        },
        setTriggers(state, action) {
            state.triggers = action.payload;
        },
        addTrigger(state, action) {
            state.triggers.push(action.payload);
        },
        updateTrigger(state, action) {
            const idx = state.triggers.findIndex((t) => t.id === action.payload.id);
            if (idx !== -1) state.triggers[idx] = action.payload;
        },
        removeTrigger(state, action) {
            state.triggers = state.triggers.filter((t) => t.id !== action.payload);
        },
        clearTriggers(state) {
            state.triggers = [];
            state.triggersLoading = false;
            state.triggerSaving = false;
            state.manualExportLoading = false;
        },

        // ── Run History ───────────────────────────────────────────────────────

        setRunsLoading(state, action) {
            state.runsLoading = action.payload;
        },

        /** payload: { data: Run[], pagination: { page, limit, total, totalPages } } */
        setRuns(state, action) {
            state.runs = action.payload.data ?? [];
            state.runsPagination = {
                page: action.payload.pagination?.page ?? 1,
                limit: action.payload.pagination?.limit ?? 10,
                total: action.payload.pagination?.total ?? 0,
                totalPages: action.payload.pagination?.totalPages ?? 0,
            };
        },

        /** Merge partial filter changes */
        setRunsFilter(state, action) {
            state.runsFilters = { ...state.runsFilters, ...action.payload };
            // Reset to page 0 whenever a filter changes
            state.runsPagination.page = 1;
        },
        resetRunsFilters(state) {
            state.runsFilters = { runMode: "", runStatus: "", triggerId: "" };
            state.runsPagination.page = 1;
        },

        setRunsPage(state, action) {
            state.runsPagination.page = action.payload;
        },
        setRunsLimit(state, action) {
            state.runsPagination.limit = action.payload;
            state.runsPagination.page = 1;
        },

        // Detail dialog
        setDetailLoading(state, action) {
            state.detailLoading = action.payload;
        },
        setDetailRun(state, action) {
            state.detailRun = action.payload;
        },
        clearDetailRun(state) {
            state.detailRun = null;
        },

        // Per-run download URL loading
        setDownloadLoading(state, action) {
            const { runId, loading } = action.payload;
            if (loading) {
                state.downloadLoadingByRun[runId] = true;
            } else {
                delete state.downloadLoadingByRun[runId];
            }
        },

        clearRunHistory(state) {
            state.runs = [];
            state.runsLoading = false;
            state.runsPagination = { page: 1, limit: 10, total: 0, totalPages: 0 };
            state.runsFilters = { runMode: "", runStatus: "", triggerId: "" };
            state.detailRun = null;
            state.detailLoading = false;
            state.downloadLoadingByRun = {};
        },
    },
});

export const exportTriggerAction = exportTriggerSlice.actions;
export const exportTriggerReducer = exportTriggerSlice.reducer;
